/**
 * `@dxf-ai-editor/cad-core` — the ONE-TRUTH *drawable topology* of a heavy
 * `POLYLINE` (@codex PR #785 round 3, F1/F2/F3).
 *
 * `./polylineMeshTopology.ts` already owns the MESH half of that question
 * (which rows / columns / face edges a polygon or poly-face mesh draws). This
 * module is the other half plus the union of the two, and it answers — once,
 * for every consumer — the three questions a heavy POLYLINE consumer kept
 * answering differently:
 *
 *   1. **Which records are visible geometry, in what order?**
 *      A spline-fit polyline (group 70 bit 4) stores BOTH the generated
 *      approximation vertices (VERTEX group 70 bit 8) and the spline FRAME
 *      control points (bit 16). AutoCAD draws the approximation; the frame is
 *      the control polygon `SPLFRAME` shows only when it is ON, and it is a
 *      SEPARATE polygon, never a continuation of the curve. Connecting the
 *      whole record run in file order therefore draws a phantom segment from
 *      the end of the curve back to the frame, plus the frame itself — which
 *      is exactly what `abi1-test.dxf` handle `97F1` (1 frame record, 121
 *      generated vertices, then 17 more frame records) produced on screen.
 *   2. **Which spans are curved?** Group 42 is a 2D-polyline field (Autodesk
 *      DXF reference), so a 3D polyline / polygon mesh / poly-face mesh has no
 *      bulges AT ALL — not "usually zero". A 2D polyline's bulge belongs to the
 *      span LEAVING its vertex, which after the spline-fit filter is no longer
 *      the raw record index, so the bulge list must travel WITH the run.
 *   3. **Where is the run in WCS?** A 2D heavy polyline's vertices are OCS
 *      coordinates in the plane of the entity's group-210 extrusion at the
 *      entity's ELEVATION (the POLYLINE's own 10/20/30 dummy point, whose Z is
 *      the elevation). A 3D polyline / mesh stores WCS vertices and must NOT be
 *      lifted. {@link heavyPolylineWcsResolver} is that rule, applied through
 *      `drawing-model`'s Arbitrary-Axis basis — the same one
 *      `wcsBoundsLeaf.ts::heavyPolylineLocal` bounds with.
 *
 * # Why here, and why INDICES
 *
 * Five consumers unpack this entity — the scene3d render adapter
 * (`apps/web/…/scene3d/adapters/typed/polylineFromEntity.ts`), the picker
 * (`./selectLeaf.ts`), the vector-PDF plot
 * (`apps/web/…/print/pdf/converters/heavyPolylineConverter.ts`), the AI
 * `osnap-query` route (`apps/web/…/ai/sceneQuery/osnapQuery.ts`, which reads the
 * SNAPSHOT FACET rather than the entity — see
 * {@link HeavyPolylineGeometrySource}) and the WCS bounds walk
 * (`drawing-model/wcsBoundsLeaf.ts`). `apps/web` already depends on
 * this package, so this is the shared home the dependency direction allows.
 *
 * A run carries INDICES into `entity.vertices`, never coordinates — the same
 * point-type-agnostic contract `./polylineMeshTopology.ts` established — so a
 * `Vec3`-building renderer, a `Vec2`-picking selector and an `Affine2D`-mapping
 * plotter all consume the identical runs without any of them imposing its point
 * type on the others.
 */

import {
  DXF_POLYLINE_FLAG,
  DXF_VERTEX_FLAG,
  computeOcsAxes,
  isDefaultExtrusion,
  ocsToWcs,
  polylineIsClosed,
  polylineSubtype,
} from "@dxf-ai-editor/drawing-model";

import {
  polylineMeshWireRuns,
  type PolylineMeshTopologySource,
} from "./polylineMeshTopology";

/**
 * One drawable run of a heavy POLYLINE.
 *
 * `indices[k]` addresses `entity.vertices`; `bulges[k]` is the bulge of the
 * span LEAVING `indices[k]` (so `bulges` is always the same length as
 * `indices`, and its LAST entry is meaningful only when `closed` — it bows the
 * wrap-around span, exactly as DXF defines it for the final vertex of a closed
 * polyline). Every entry is 0 for a run that cannot carry arcs, so a consumer
 * never has to re-derive "is this subtype allowed to bulge?".
 */
export interface HeavyPolylineRun {
  /** Stable per-entity run id ("run-0" / "row-0" / "face-1-edge-3"). */
  readonly id: string;
  readonly indices: readonly number[];
  readonly closed: boolean;
  readonly bulges: readonly number[];
}

/**
 * The minimum a caller must supply to resolve the VISIBLE record run: the
 * entity's group-70 flags and each record's own group-70 flags.
 *
 * Structural, not `PolylineEntity`, so the SNAPSHOT FACET the AI `osnap-query`
 * route reads (`state/captureEntityGeometry.ts`'s `"Polyline"` facet, which
 * copies both flag channels verbatim but is not the entity) consumes the same
 * one truth instead of re-deriving the rule — or, as it did, skipping it.
 * `PolylineEntity` satisfies this shape as-is.
 */
export interface HeavyPolylineRecordFlags {
  readonly flags: number;
  readonly vertices: readonly { readonly flags?: number | undefined }[];
}

/**
 * Everything a caller must supply to resolve BOTH halves of this module —
 * {@link heavyPolylineRuns} (which records are visible geometry, in what order,
 * which spans are curved) and {@link heavyPolylineWcsResolver} (where that run
 * lands in WCS): the mesh channels of {@link PolylineMeshTopologySource}, plus
 * each record's own location and bulge, plus the entity's elevation and its
 * group-210 extrusion.
 *
 * Structural for the same reason {@link HeavyPolylineRecordFlags} is, and now
 * for a second consumer as well: the SNAPSHOT FACET the AI `osnap-query` route
 * reads (`apps/web/.../state/captureEntityGeometry.ts`'s `"Polyline"` facet)
 * carries every one of these channels VERBATIM — flags, per-record flags and
 * face indices, per-record location and bulge, elevation, the mesh counts and
 * the extrusion — but is not a `PolylineEntity`, so only a structural parameter
 * lets it consume this one truth rather than copying raw OCS `x`/`y` out of the
 * facet as if they were WCS (@codex PR #785 round 4, F2/F3).
 * `PolylineEntity` satisfies this shape as-is, so no existing caller changes.
 */
export interface HeavyPolylineGeometrySource
  extends PolylineMeshTopologySource {
  readonly vertices: readonly {
    readonly location: {
      readonly x: number;
      readonly y: number;
      readonly z?: number | undefined;
    };
    readonly bulge?: number | undefined;
    readonly flags?: number | undefined;
    readonly faceVertexIndices?: readonly number[] | undefined;
  }[];
  /**
   * The POLYLINE's own group-10/20/30 "dummy point" — its Z is the ELEVATION of
   * a 2D polyline's plane (Autodesk DXF reference), which is why only `z` is
   * required here.
   */
  readonly elevation: { readonly z?: number | undefined };
  /** DXF group 210/220/230 — OCS extrusion normal; absent ⇒ (0, 0, 1). */
  readonly extrusion?:
    | { readonly x: number; readonly y: number; readonly z: number }
    | undefined;
}

/** Group 70 bit 4 — spline-fit vertices have been added to this polyline. */
function isSplineFit(entity: HeavyPolylineRecordFlags): boolean {
  return (entity.flags & DXF_POLYLINE_FLAG.SPLINE_FIT) !== 0;
}

/**
 * Indices of the records that form the VISIBLE run of a 2D / 3D heavy
 * polyline, in draw order.
 *
 * Normally every record: a plain polyline draws its vertices as authored, and
 * a CURVE-fit polyline (group 70 bit 2) draws all of them too — its generated
 * records (VERTEX bit 1, "extra vertex created by curve-fitting") lie ON the
 * fitted curve between the user's own vertices and carry the bulges that bow
 * it, so dropping either kind would erase real drawn geometry.
 *
 * The ONE exception is SPLINE-fit (bit 4): there the generated records (VERTEX
 * bit 8) are the drawn approximation and the bit-16 records are the control
 * FRAME, which lies off the curve. The filter applies only when the entity
 * actually carries generated vertices — a bit-4 polyline that stores only its
 * frame (`SPLINEDIT`-produced, or a producer that wrote the frame alone) still
 * draws that frame rather than vanishing, which is what AutoCAD shows for it.
 *
 * Mesh subtypes never reach here: their record→run mapping is
 * {@link polylineMeshWireRuns}, whose own FACE-record filter is the one-truth
 * `dxfVertexSubtype` decode.
 */
export function heavyPolylineVisibleIndices(
  entity: HeavyPolylineRecordFlags,
): number[] {
  const all = entity.vertices.map((_, i) => i);
  if (!isSplineFit(entity)) return all;
  const generated = all.filter(
    (i) =>
      ((entity.vertices[i]!.flags ?? 0) & DXF_VERTEX_FLAG.SPLINE_VERTEX) !== 0,
  );
  if (generated.length === 0) return all;
  return generated;
}

/**
 * The drawable runs of ANY heavy POLYLINE subtype.
 *
 * A mesh yields the {@link polylineMeshWireRuns} rows / columns / face edges
 * (bulge-free by construction) — including, for a SMOOTHED polygon mesh, the
 * generated surface grid rather than its control frame, which is that module's
 * own application of the same bit-8 / bit-16 rule
 * {@link heavyPolylineVisibleIndices} states below. A 2D / 3D polyline yields
 * a single run over {@link heavyPolylineVisibleIndices}, carrying the per-span
 * bulges for a 2D polyline and zeros for a 3D one.
 *
 * A run with fewer than two indices is still returned (callers that need at
 * least a segment drop it themselves); nothing here fabricates or discards
 * geometry the file does not have.
 */
export function heavyPolylineRuns(
  entity: HeavyPolylineGeometrySource,
): HeavyPolylineRun[] {
  const meshRuns = polylineMeshWireRuns(entity);
  if (meshRuns) {
    return meshRuns.map((run) => ({
      id: run.id,
      indices: run.indices,
      closed: run.closed,
      bulges: run.indices.map(() => 0),
    }));
  }
  const indices = heavyPolylineVisibleIndices(entity);
  // Group 42 is a 2D-POLYLINE field (Autodesk DXF reference), so a 3D polyline
  // has no bulges BY CONSTRUCTION. `bulge ?? 0` is mandatory, not cosmetic:
  // `bulgeArc`'s "is this span straight?" guard is `Math.abs(bulge) < EPS`, and
  // `Math.abs(undefined)` is NaN, which fails that comparison and poisons the
  // whole span (`wcsBoundsLeaf.ts::heavyPolylineLocal` hit the same trap).
  const is2d = polylineSubtype(entity) === "2d";
  return [
    {
      id: "run-0",
      indices,
      closed: polylineIsClosed(entity),
      bulges: indices.map((i) =>
        is2d ? (entity.vertices[i]!.bulge ?? 0) : 0,
      ),
    },
  ];
}

/** A WCS point, structurally compatible with every consumer's own `Vec3`. */
export interface HeavyPolylineWcsPoint {
  x: number;
  y: number;
  z: number;
}

/**
 * The entity's record-index → WCS-point resolver.
 *
 * A **2D polyline** is PLANAR: its plane is the entity's elevation and its
 * vertex X/Y are OCS coordinates in the group-210 extrusion plane, so the point
 * is `x·Ax + y·Ay + elevation·N` (Arbitrary Axis Algorithm). A VERTEX's own
 * group 30 is an OCS duplicate producers routinely write as 0 — and
 * `drawingPoint3DSchema` materialises it with `.default(0)` — so it is never
 * the Z source for this subtype.
 *
 * A **3D polyline / polygon mesh / poly-face mesh** is the opposite: its
 * per-vertex Z is real WCS data, the elevation means nothing, and applying an
 * OCS tilt to already-WCS vertices would MOVE the entity (the same rule
 * `heavyPolylineLocal` states by reporting `extrusion: undefined` for them).
 *
 * A default / absent extrusion short-circuits to the identity basis, so the
 * overwhelmingly common planar case costs one branch and no matrix math.
 * An out-of-range index resolves to the origin rather than throwing — a run
 * index is always in range through {@link heavyPolylineRuns}, but a direct
 * caller must not be able to crash the render on a malformed record.
 */
export function heavyPolylineWcsResolver(
  entity: HeavyPolylineGeometrySource,
): (index: number) => HeavyPolylineWcsPoint {
  const finite = (n: number): number => (Number.isFinite(n) ? n : 0);
  if (polylineSubtype(entity) !== "2d") {
    return (index) => {
      const v = entity.vertices[index];
      if (!v) return { x: 0, y: 0, z: 0 };
      return {
        x: finite(v.location.x),
        y: finite(v.location.y),
        z: finite(v.location.z ?? 0),
      };
    };
  }
  const elevation = finite(entity.elevation.z ?? 0);
  // NOT an equivalent mutant — corrected @codex PR #785 round 4. This branch was
  // documented here as a pure allocation/arithmetic optimisation whose removal
  // no test could observe, on the reasoning that `computeOcsAxes(default |
  // undefined)` IS the identity basis and `ocsToWcs({x, y, elevation},
  // identity)` is `{x, y, elevation}`. That is true only up to SIGNED ZERO: the
  // general path computes each component as a sum of products, and
  // `-0 * 1 + 0 * 0 + 0 * 0` is `+0`, so routing a coordinate authored as `-0`
  // (or an elevation of `-0`) through it silently normalises the sign of the
  // zero. This branch returns the authored value untouched. The
  // `ConditionalExpression → false` mutant is therefore KILLED, by
  // `./heavyPolylineSelectOcs.metamorphic.test.ts`'s "is the IDENTITY for the
  // default extrusion" property (its coordinate generator includes `-0`
  // deliberately). Keeping the branch also keeps the overwhelmingly common
  // planar case at one branch and no matrix math, which is why it was written.
  if (isDefaultExtrusion(entity.extrusion)) {
    return (index) => {
      const v = entity.vertices[index];
      if (!v) return { x: 0, y: 0, z: elevation };
      return { x: finite(v.location.x), y: finite(v.location.y), z: elevation };
    };
  }
  const axes = computeOcsAxes(entity.extrusion);
  return (index) => {
    const v = entity.vertices[index];
    const local = v
      ? { x: finite(v.location.x), y: finite(v.location.y), z: elevation }
      : { x: 0, y: 0, z: elevation };
    return ocsToWcs(local, axes);
  };
}
